export const profile = {
  name: "Nasim Khodapanah",
  title: "Lead Full-Stack Software Developer",
  location: "Toronto, ON",
  email: "khodapanah.nasim@gmail.com",
  phone: "(416) 890-6461",
  linkedin: "https://linkedin.com/in/nasim-khodapanah",
  github: "https://github.com/NasimKhodapanah",
  website: "https://nasim-khodapanah.vercel.app",
  resumeTextUrl: "/Nasim_Khodapanah_Resume_ATS.txt",
  resumePdfUrl: "/Nasim_Khodapanah_Resume.pdf",
  summary:
    "Full-Stack Software Developer with 4+ years of experience building scalable cloud-native products across fintech, media, and AI-driven platforms. Specialized in serverless architecture, payment systems, and workflow automation that improve delivery speed and product reliability.",
  resumeNote:
    "I build products that balance visual quality, scalability, and delivery speed. My work spans design-focused frontend interfaces, production backend systems, and cloud automation.",
};

export const highlights = [
  { label: "Years Experience", value: "4+" },
  { label: "Team Velocity Gain", value: "+30%" },
  { label: "Manual Effort Reduced", value: "-60%" },
  { label: "Digital Titles / Week", value: "200-300" },
  { label: "Users Supported", value: "10,000+" },
];

export const experiences = [
  {
    company: "Kampus Media Inc.",
    role: "Lead Software Developer",
    period: "Jan 2024 - Present",
    location: "Montreal, QC",
    theme: "from-fuchsia-500 via-purple-500 to-indigo-500",
    achievements: [
      "Led sprint planning, code reviews, and technical mentorship to increase team delivery velocity by 30%.",
      "Architected a unified React + TypeScript dashboard for PDF-to-audiobook workflows and multi-platform distribution.",
      "Built Python-based automation with LLM OCR and TTS APIs, cutting manual production effort by more than 60%.",
      "Designed serverless services with AWS Lambda and Cloudflare Workers using secure JWT and API-key authentication.",
      "Implemented ONIX distribution pipelines for Apple Books and Google Play to eliminate manual publishing bottlenecks.",
    ],
    stack: [
      "React",
      "TypeScript",
      "Python",
      "AWS Lambda",
      "Cloudflare Workers",
      "Jenkins",
      "Docker",
    ],
    impact: ["+30% team delivery velocity", "60%+ manual effort reduction", "200-300 titles/week"],
  },
  {
    company: "Merlin Payments Solutions",
    role: "Full Stack Developer",
    period: "Jun 2022 - Aug 2023",
    location: "Toronto, ON",
    theme: "from-emerald-400 via-cyan-400 to-blue-500",
    achievements: [
      "Improved onboarding completion by 70% by integrating AWS SNS alerts and third-party KYC verification.",
      "Developed MerlinPay using Vue.js, Node.js, and REST APIs for peer-to-peer transfers and real-time payment tracking.",
      "Built a secure kiosk dashboard for cash-to-digital conversion with low-latency synchronization across sessions.",
      "Delivered a Flutter POS application with QR payments, offline resilience, and automated receipt generation.",
    ],
    stack: ["Vue.js", "Node.js", "Flutter", "AWS SNS", "REST APIs"],
    impact: ["+70% onboarding completion", "Real-time payment tracking", "Offline-first POS workflows"],
  },
  {
    company: "FaraomranNegar",
    role: "Front-End Developer",
    period: "Dec 2018 - Dec 2020",
    location: "Tehran, Iran",
    theme: "from-orange-400 via-rose-400 to-pink-500",
    achievements: [
      "Developed responsive React interfaces and reusable component patterns that reduced new page delivery time by 40%.",
    ],
    stack: ["React", "JavaScript", "Responsive Design"],
    impact: ["40% faster page delivery", "Reusable component architecture"],
  },
];

export const educations = [
  {
    degree: "Diploma in Computer Programming",
    school: "Seneca College",
    period: "Jan 2021 - Apr 2022",
  },
  {
    degree: "B.Sc. in Computer Software Engineering",
    school: "Central Tehran Branch University",
    period: "Sep 2016 - Apr 2020",
  },
];

export const skillGroups = [
  {
    category: "Languages",
    color: "bg-gradient-to-r from-pink-500 to-rose-500",
    items: [
      { name: "Python", level: 90 },
      { name: "JavaScript", level: 92 },
      { name: "TypeScript", level: 88 },
      { name: "Dart", level: 78 },
      { name: "SQL", level: 85 },
      { name: "HTML/CSS", level: 94 },
    ],
  },
  {
    category: "Frontend",
    color: "bg-gradient-to-r from-pink-500 to-rose-500",
    items: [
      { name: "React", level: 93 },
      { name: "Vue.js", level: 84 },
      { name: "Angular", level: 72 },
      { name: "Flutter", level: 80 },
      { name: "Tailwind CSS", level: 90 },
      { name: "Responsive Design", level: 95 },
    ],
  },
  {
    category: "Backend",
    color: "bg-gradient-to-r from-pink-500 to-rose-500",
    items: [
      { name: "Node.js", level: 88 },
      { name: "Express", level: 86 },
      { name: "NestJS", level: 77 },
      { name: "REST APIs", level: 92 },
      { name: "GraphQL", level: 74 },
      { name: "JWT Auth", level: 89 },
    ],
  },
  {
    category: "Cloud & DevOps",
    color: "bg-gradient-to-r from-pink-500 to-rose-500",
    items: [
      { name: "AWS", level: 87 },
      { name: "Cloudflare", level: 80 },
      { name: "Jenkins", level: 78 },
      { name: "CI/CD", level: 84 },
      { name: "Docker", level: 82 },
      { name: "Git", level: 91 },
    ],
  },
  {
    category: "Data & Tools",
    color: "bg-gradient-to-r from-pink-500 to-rose-500",
    items: [
      { name: "PostgreSQL", level: 82 },
      { name: "MongoDB", level: 80 },
      { name: "MySQL", level: 79 },
      { name: "Redis", level: 73 },
      { name: "Postman", level: 90 },
      { name: "Jira", level: 88 },
      { name: "Agile/Scrum", level: 90 },
    ],
  },
];

export const resumeFeatures = [
  {
    title: "Cloud-Native Architecture",
    detail: "Serverless systems using AWS Lambda + Cloudflare Workers for reliable high-volume workflows.",
  },
  {
    title: "Automation & AI Workflows",
    detail: "Python automation with OCR/LLM/TTS integrations to reduce manual operations and accelerate releases.",
  },
  {
    title: "Payment Systems Engineering",
    detail: "Real-time fintech applications with KYC flows, POS transactions, QR payments, and secure APIs.",
  },
  {
    title: "Team Leadership",
    detail: "Sprint planning, mentorship, and code review leadership that improves velocity and engineering quality.",
  },
];

export const focusAreas = [
  "Scalable full-stack architecture",
  "Design-forward product interfaces",
  "Workflow automation and CI/CD",
  "Secure API and auth implementation",
];

export const caseStudies = [
  {
    title: "AI-Powered Publishing Automation",
    subtitle: "Kampus Media Inc.",
    challenge:
      "Manual audiobook production and distribution slowed release cycles and made metadata quality difficult to scale.",
    solution:
      "Designed a unified React + TypeScript operations dashboard with two core pipelines: a TTS pipeline that converts PDF files into audio, and a Distributor pipeline that publishes content across major platforms.",
    architecture: [
      "React + TypeScript dashboard for operator workflows",
      "TTS pipeline: PDF to audio conversion using Python and JavaScript backend services",
      "Distributor pipeline: distribution to Google, Apple, Audible, and Spotify",
      "AWS Lambda + Cloudflare Workers for scalable orchestration",
      "PostgreSQL for structured workflow and metadata persistence",
      "AWS S3 and Cloudflare buckets for content and media storage",
      "REST integrations for ONIX delivery to Apple Books and Google Play",
    ],
    results: ["60%+ manual effort reduction", "200-300 titles processed weekly", "Faster, more reliable release cycle"],
    tags: ["React", "TypeScript", "Python", "AWS", "Cloudflare", "CI/CD"],
    color: "from-fuchsia-500 to-indigo-500",
  },
  {
    title: "MerlinPay Fintech Platform",
    subtitle: "Merlin Payments Solutions",
    challenge:
      "The team needed secure digital onboarding and real-time payment experiences across web, kiosk, and mobile platforms.",
    solution:
      "Built full-stack payment flows with Vue.js and Node.js APIs, integrated KYC providers and AWS SNS notifications, and delivered cross-platform POS capabilities using Flutter.",
    architecture: [
      "Vue.js web dashboards for B2B/B2C users",
      "Node.js API services for payment orchestration",
      "AWS SNS for status and onboarding notifications",
      "Flutter POS app with offline resilience and QR payment support",
    ],
    results: ["70% onboarding completion improvement", "Real-time transaction visibility", "Reliable multi-channel payment workflows"],
    tags: ["Vue.js", "Node.js", "Flutter", "AWS SNS", "REST APIs"],
    color: "from-cyan-500 to-blue-500",
  },
];

export const liveProjects = [
  {
    name: "North York Food Bank Scheduler",
    url: "https://food-bank-registry.vercel.app",
    summary:
      "A community platform where clients can apply once, admins approve requests, and families book Wednesday pickup slots without back-and-forth emails.",
    highlights: ["60+ families served weekly", "Client/Admin workflows", "Capacity and attendance management"],
    stack: ["React", "Dashboard UX", "Scheduling Logic", "Admin Workflow"],
    accent: "from-emerald-500 to-cyan-500",
  },
  {
    name: "Kampus Unified Dashboard",
    url: "https://37b8aa5c.kampus-frontend.pages.dev/dashboard",
    summary:
      "Unified dashboard for two operational pipelines: TTS converts PDF to audio files, and Distributor publishes titles to Google, Apple, Audible, and Spotify.",
    highlights: [
      "TTS pipeline for PDF-to-audio conversion",
      "Distributor pipeline for Google/Apple/Audible/Spotify publishing",
      "Cloudflare Workers + AWS services with PostgreSQL, S3, and Cloudflare buckets",
    ],
    stack: ["React", "TypeScript", "Python", "JavaScript", "Cloudflare Workers", "AWS", "PostgreSQL", "S3"],
    accent: "from-fuchsia-500 to-violet-500",
  },
  {
    name: "Zip Uploader",
    url: "https://zip-uploader-frontend.andrea-4a3.workers.dev/upload",
    summary:
      "Modern zip-file upload experience with drag-and-drop, upload progress, file listing, sorting controls, and in-browser preview.",
    highlights: ["Drag-and-drop upload", "Progress + file table UX", "Preview and open-in-tab workflow"],
    stack: ["Frontend", "Cloudflare Workers", "File Handling", "UX Design"],
    accent: "from-amber-500 to-rose-500",
  },
];



// WEBPACK FOOTER //
// ./src/data.js